import { PresenceDetector, PresenceDetectorSource } from '../services/presenceDetector';

export type PresenceState = 'warming-up' | 'present' | 'possibly-absent' | 'absent-confirmed' | 'recovering' | 'unavailable';
export type PresenceCapability = 'checking' | 'active' | 'degraded' | 'unavailable';

export interface PresenceConfig {
  sampleIntervalMs: number;
  startupGracePeriodMs: number;
  requiredConsecutiveAbsentSamples: number;
  absenceConfirmationMs: number;
  requiredConsecutivePresentSamples: number;
  recoveryDurationMs: number;
  detectorRetryDelayMs: number;
  maximumConsecutiveDetectorFailures: number;
  capabilityRecoveryRetryMs: number;
}

export interface PresenceDiagnostics {
  detector?: PresenceDetectorSource;
  detectorReady: boolean;
  lastSuccessfulDetectionAt?: number;
  state: PresenceState;
  failureCount: number;
}

export interface PresenceUpdate extends PresenceDiagnostics {
  capability: PresenceCapability;
  absence?: boolean;
}

export type PresenceDetectorFactory = (preferFallback: boolean) => Promise<PresenceDetector>;
type DiagnosticsLogger = (event: string, diagnostics: PresenceDiagnostics) => void;

export class CandidatePresenceMonitor {
  private detector?: PresenceDetector;
  private state: PresenceState = 'warming-up';
  private capability: PresenceCapability = 'checking';
  private failureCount = 0;
  private lastSuccessfulDetectionAt?: number;
  private lastAttemptAt = Number.NEGATIVE_INFINITY;
  private nextAttemptAt = 0;
  private absentSince?: number;
  private absentSamples = 0;
  private presentSince?: number;
  private presentSamples = 0;
  private preferFallback = false;
  private busy = false;
  private disposed = false;
  private unavailableLogged = false;

  constructor(
    private readonly config: PresenceConfig,
    private readonly createDetector: PresenceDetectorFactory,
    private readonly sessionStartedAt: number,
    private readonly logger?: DiagnosticsLogger
  ) {}

  async sample(video: HTMLVideoElement | null, now: number): Promise<PresenceUpdate> {
    if (this.disposed) return this.snapshot();
    if (this.busy || now < this.nextAttemptAt || now - this.lastAttemptAt < this.config.sampleIntervalMs) return this.snapshot();
    if (now - this.sessionStartedAt < this.config.startupGracePeriodMs || !videoReady(video)) {
      this.transition('warming-up');
      return this.snapshot();
    }

    this.busy = true;
    this.lastAttemptAt = now;
    try {
      if (!this.detector) {
        this.detector = await this.createDetector(this.preferFallback);
        this.logger?.('detector selected', this.snapshot());
      }
      const present = await this.detector.detect(video);
      this.failureCount = 0;
      this.capability = 'active';
      this.lastSuccessfulDetectionAt = now;
      if (this.unavailableLogged) this.logger?.('detector recovered', this.snapshot());
      this.unavailableLogged = false;
      const absence = this.updatePresence(present, now);
      return { ...this.snapshot(), absence };
    } catch {
      if (!this.disposed) await this.handleFailure(now);
      return this.snapshot();
    } finally {
      this.busy = false;
    }
  }

  async dispose(): Promise<void> {
    this.disposed = true;
    await this.releaseDetector();
    this.busy = false;
  }

  private async releaseDetector(): Promise<void> {
    const detector = this.detector;
    this.detector = undefined;
    if (detector) await detector.dispose();
  }

  getDiagnostics(): PresenceDiagnostics {
    return this.snapshot();
  }

  private updatePresence(present: boolean, now: number): boolean | undefined {
    if (!present) {
      this.presentSamples = 0;
      this.presentSince = undefined;
      if (this.state === 'absent-confirmed' || this.state === 'recovering') {
        this.transition('absent-confirmed');
        return true;
      }
      this.absentSince ??= now;
      this.absentSamples += 1;
      const confirmed = this.absentSamples >= this.config.requiredConsecutiveAbsentSamples
        && now - this.absentSince >= this.config.absenceConfirmationMs;
      this.transition(confirmed ? 'absent-confirmed' : 'possibly-absent');
      return confirmed ? true : undefined;
    }

    this.absentSamples = 0;
    this.absentSince = undefined;
    if (this.state === 'absent-confirmed' || this.state === 'recovering') {
      this.presentSince ??= now;
      this.presentSamples += 1;
      const recovered = this.presentSamples >= this.config.requiredConsecutivePresentSamples
        && now - this.presentSince >= this.config.recoveryDurationMs;
      this.transition(recovered ? 'present' : 'recovering');
      if (recovered) {
        this.presentSamples = 0;
        this.presentSince = undefined;
      }
      return recovered ? false : true;
    }

    this.presentSamples = 0;
    this.presentSince = undefined;
    this.transition('present');
    return false;
  }

  private async handleFailure(now: number): Promise<void> {
    this.failureCount += 1;
    const previousCapability = this.capability;
    this.capability = this.failureCount >= this.config.maximumConsecutiveDetectorFailures ? 'unavailable' : 'degraded';
    this.transition('unavailable');
    this.nextAttemptAt = now + this.config.detectorRetryDelayMs;
    if (!this.unavailableLogged || (this.capability === 'unavailable' && previousCapability !== 'unavailable')) {
      this.logger?.(this.capability === 'unavailable' ? 'detector unavailable' : 'detector temporarily failed', this.snapshot());
      this.unavailableLogged = true;
    }
    if (this.failureCount < this.config.maximumConsecutiveDetectorFailures) return;

    const failedSource = this.detector?.source;
    await this.releaseDetector();
    if (failedSource === 'native') this.preferFallback = true;
    this.nextAttemptAt = now + (failedSource === 'native' ? this.config.detectorRetryDelayMs : this.config.capabilityRecoveryRetryMs);
  }

  private transition(state: PresenceState): void {
    if (this.state === state) return;
    this.state = state;
    this.logger?.('state changed', this.snapshot());
  }

  private snapshot(): PresenceUpdate {
    return {
      detector: this.detector?.source,
      detectorReady: this.capability === 'active',
      lastSuccessfulDetectionAt: this.lastSuccessfulDetectionAt,
      state: this.state,
      failureCount: this.failureCount,
      capability: this.capability
    };
  }
}

export function videoReady(video: HTMLVideoElement | null): video is HTMLVideoElement {
  return Boolean(video && video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0);
}
